import React, { useContext } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { motion, useReducedMotion } from 'framer-motion';
import Icon from './icons';
import { UserContext } from '../../context/UserContext';
import { closeDay, closingOf, closingReasonText, schoolOf, stillHereText } from './leavingSchool';
import './ClosingBanner.css';

// C4 "Leaving school": while the guidance office is closing this account (user.leaving.state
// 'closing' from /auth/user/), a calm banner under the student header on every student page
// (Navbar.js) and on the home page with the chat (Header.js): the close date, why, "Still at
// UIC? Tell the guidance office." and "Save my results", which goes to the card at the top of
// Results (studentResults/KeepResultsCard.js, id "keep-results").

export const KEEP_RESULTS_ID = 'keep-results';

const onResultsPage = (pathname) => /^\/results?\/?$/.test(pathname);

export default function ClosingBanner() {
  const { user } = useContext(UserContext) || {};
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const reduce = useReducedMotion();
  const leaving = closingOf(user);
  if (!leaving) return null;

  const day = closeDay(leaving.closes_on);
  const school = schoolOf(user, leaving);

  const save = () => {
    if (onResultsPage(pathname)) {
      const card = document.getElementById(KEEP_RESULTS_ID);
      if (card) {
        card.scrollIntoView({ behavior: reduce ? 'auto' : 'smooth', block: 'center' });
        card.focus({ preventScroll: true });
        return;
      }
    }
    navigate(`/result#${KEEP_RESULTS_ID}`);
  };

  return (
    <motion.aside
      className="cb-banner"
      aria-labelledby="cb-banner-title"
      initial={reduce ? false : { opacity: 0, y: -8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: reduce ? 0 : 0.3, ease: [0.16, 1, 0.3, 1] }}
    >
      <div className="cb-inner">
        <span className="cb-tile" aria-hidden="true">
          <Icon name="calendar-x" size={22} />
        </span>
        <div className="cb-text">
          <p className="cb-title" id="cb-banner-title">
            {day ? `Your ANNA account closes on ${day}` : 'Your ANNA account is closing soon'}
          </p>
          <p className="cb-body">
            {closingReasonText(leaving, school)} {stillHereText(school)}
          </p>
        </div>
        <button type="button" className="cb-btn" onClick={save}>
          <Icon name="download" size={18} />
          Save my results
        </button>
      </div>
    </motion.aside>
  );
}
